import { Headphones } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Table, type Column } from '@/shared/components/ui/Table';
import { useCallCenter } from '../hooks/useAnalytics';
import { useEmployeeLabels } from '../hooks/useEmployeeLabels';
import { PanelState } from './PanelState';
import { formatCount, formatDuration } from '../constants';
import type { EmployeeCallStats, BestWindow } from '../types';

/** Best-effort label for a best-call-window row (shape comes from the optimizer). */
function windowLabel(w: BestWindow): string {
  if (w.label) return String(w.label);
  if (w.hour != null) {
    const h = Number(w.hour);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12}:00 ${suffix}`;
  }
  return '—';
}

export function CallCenterPanel({ enabled }: { enabled: boolean }) {
  const { data, isLoading, isError } = useCallCenter({ enabled });
  const { resolve } = useEmployeeLabels(enabled);

  const columns: Column<EmployeeCallStats>[] = [
    {
      key: 'employee',
      header: 'Employee',
      render: (r) => {
        const { name, tier } = resolve(r.userId);
        return (
          <div className="flex flex-col">
            <span className="font-medium text-on-surface">{name}</span>
            {tier && <span className="text-label-caps uppercase text-on-surface-variant">{tier}</span>}
          </div>
        );
      },
    },
    { key: 'callsToday', header: 'Calls today', align: 'right', render: (r) => formatCount(r.callsToday) },
    { key: 'talked', header: 'Talked', align: 'right', render: (r) => formatCount(r.talked) },
    {
      key: 'avg',
      header: 'Avg duration',
      align: 'right',
      render: (r) => (r.callsToday > 0 ? formatDuration(r.avgDurationSec) : '—'),
    },
  ];

  const bestWindows = data?.bestWindows ?? [];

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <Headphones className="h-4 w-4" /> Call Center Productivity
          </span>
        }
        subtitle="Per-employee call activity today, plus best-call windows from the optimizer."
      />
      <CardBody className="space-y-4">
        <PanelState
          isLoading={isLoading}
          isError={isError}
          isEmpty={!!data?.empty}
          emptyTitle="No calls today"
          emptyDescription="No calls have been logged today for the current scope."
          emptyIcon={<Headphones className="h-6 w-6" />}
        >
          <Table
            columns={columns}
            data={data?.byEmployee ?? []}
            rowKey={(r) => r.userId}
            empty="No call activity."
          />

          {bestWindows.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-data-md font-semibold text-on-surface">Best call windows</h4>
              <div className="flex flex-wrap gap-2">
                {bestWindows.map((w, i) => (
                  <Badge key={i} tone="success">
                    {windowLabel(w)}
                    {w.successRate != null ? ` · ${Math.round(Number(w.successRate) * (Number(w.successRate) <= 1 ? 100 : 1))}%` : ''}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </PanelState>
      </CardBody>
    </Card>
  );
}
